import React, { useState, useEffect } from 'react';
import { useGym } from '../../contexts/GymContext';
import { useSearchParams } from 'react-router-dom';
import LoadingSpinner from '../common/LoadingSpinner';
import Button from '../common/Button';
import QRCode from 'qrcode';
import AdminHeader from './AdminHeader';

const BoardQRCodes = () => {
  const { selectedGym } = useGym();
  const [searchParams] = useSearchParams();
  const boardIdParam = searchParams.get('board');

  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [qrCodes, setQrCodes] = useState({});
  const [generatingQR, setGeneratingQR] = useState({});

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
  const FRONTEND_URL = process.env.REACT_APP_FRONTEND_URL || window.location.origin;

  useEffect(() => {
    if (selectedGym) {
      fetchBoards();
    }
  }, [selectedGym]);

  const fetchBoards = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/boards/gym/${selectedGym.gymId}/manage`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch boards');
      }

      const data = await response.json();
      setBoards(data);

      // Auto-generate QR for specific board if provided in URL
      if (boardIdParam) {
        const boardId = parseInt(boardIdParam);
        if (data.find(b => b.id === boardId)) {
          setTimeout(() => generateQRCode(boardId), 100);
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const generateQRCode = async (boardId) => {
    setGeneratingQR(prev => ({ ...prev, [boardId]: true }));

    try {
      // QR code links to the quick access view for streamlined check-in and reservation
      const url = `${FRONTEND_URL}/qr/board/${boardId}`;

      const qrDataUrl = await QRCode.toDataURL(url, {
        width: 400,
        margin: 2,
        color: {
          dark: '#1F2937',
          light: '#FFFFFF'
        }
      });

      setQrCodes(prev => ({
        ...prev,
        [boardId]: { url, qrDataUrl }
      }));
    } catch (err) {
      console.error('Error generating QR code:', err);
      alert('Failed to generate QR code');
    } finally {
      setGeneratingQR(prev => ({ ...prev, [boardId]: false }));
    }
  };

  const downloadQRCode = (boardId, boardName) => {
    const qrData = qrCodes[boardId];
    if (!qrData) return;

    const link = document.createElement('a');
    link.download = `${boardName.replace(/\s+/g, '_')}_QR_Code.png`;
    link.href = qrData.qrDataUrl;
    link.click();
  };

  const printQRCode = (boardId) => {
    const qrData = qrCodes[boardId];
    const board = boards.find(b => b.id === boardId);
    if (!qrData || !board) return;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>QR Code - ${board.name}</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              min-height: 100vh;
              margin: 0;
              padding: 20px;
            }
            .qr-container {
              text-align: center;
              border: 2px solid #333;
              padding: 30px;
              border-radius: 10px;
            }
            h1 {
              margin: 0 0 10px 0;
              font-size: 32px;
            }
            .board-type {
              color: #666;
              font-size: 18px;
              margin: 5px 0 20px 0;
            }
            img {
              display: block;
              margin: 20px auto;
            }
            .instructions {
              margin-top: 20px;
              font-size: 16px;
              color: #444;
            }
            @media print {
              body {
                background: white;
              }
            }
          </style>
        </head>
        <body>
          <div class="qr-container">
            <h1>${board.name}</h1>
            <div class="board-type">${board.type} Board${board.angle ? ` - ${board.angle}°` : ''}</div>
            <img src="${qrData.qrDataUrl}" alt="QR Code" />
            <div class="instructions">
              <strong>Scan to view schedule and make reservations</strong>
            </div>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();

    // Wait for image to load before printing
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="large" text="Loading boards..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-red-600">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <AdminHeader
          title="Board QR Codes"
          description="Generate QR codes for your boards. Print and mount them near each board for easy access to schedules."
        />

        {boards.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <p className="text-gray-500">No boards found.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {boards.map((board) => (
              <div key={board.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{board.name}</h3>
                      <p className="text-sm text-gray-500 capitalize">
                        {board.type} {board.angle && `• ${board.angle}°`}
                      </p>
                    </div>
                  </div>

                  {qrCodes[board.id] ? (
                    <div className="space-y-4">
                      <div className="bg-white p-4 border-2 border-gray-200 rounded-lg">
                        <img
                          src={qrCodes[board.id].qrDataUrl}
                          alt={`QR Code for ${board.name}`}
                          className="w-full h-auto"
                        />
                      </div>

                      <div className="text-xs text-gray-500 break-all bg-gray-50 p-2 rounded">
                        {qrCodes[board.id].url}
                      </div>

                      <div className="flex gap-2">
                        <button
                          onClick={() => printQRCode(board.id)}
                          className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
                        >
                          Print
                        </button>
                        <button
                          onClick={() => downloadQRCode(board.id, board.name)}
                          className="flex-1 bg-gray-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-700 transition-colors"
                        >
                          Download
                        </button>
                      </div>

                      <button
                        onClick={() => setQrCodes(prev => {
                          const updated = { ...prev };
                          delete updated[board.id];
                          return updated;
                        })}
                        className="w-full text-sm text-gray-500 hover:text-gray-700"
                      >
                        Hide QR Code
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => generateQRCode(board.id)}
                      disabled={generatingQR[board.id]}
                      className="w-full bg-blue-600 text-white px-4 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {generatingQR[board.id] ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          Generating...
                        </>
                      ) : (
                        <>
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                          </svg>
                          View QR Code
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Info Section */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-blue-900 mb-3">How to use Board QR Codes</h3>
          <ul className="space-y-2 text-sm text-blue-800">
            <li className="flex items-start gap-2">
              <svg className="w-5 h-5 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>Generate a QR code for each board</span>
            </li>
            <li className="flex items-start gap-2">
              <svg className="w-5 h-5 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>Print the QR code (recommended: laminate for durability)</span>
            </li>
            <li className="flex items-start gap-2">
              <svg className="w-5 h-5 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>Mount it near the corresponding board</span>
            </li>
            <li className="flex items-start gap-2">
              <svg className="w-5 h-5 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>Users can scan to view the schedule and make reservations</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default BoardQRCodes;
